// React core and hooks
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

// Redux selectors and actions
import { selectPatientId } from "../../../../../../redux/features/patientState/patientStateSlice";
import {
  setActiveMenu,
  setActivePage,
} from "../../../../../../redux/features/applicationState/applicationStateSlice";

// API configuration
import useAxios from "../../../../../../hooks/useAxios";
import multipartFormatAuth from "../../../../../../apis/multipartFormatAuth";
import jsonFormatAuth from "../../../../../../apis/jsonFormatAuth";

// API helpers
import {
  appendPhotosToFormData,
  processPhotoEntryImages,
  sanitizePhotoEntryDataResponse,
} from "../../../../../../helpers/apiUtils/photoUtils";
import { manageTagsSentResponse } from "../../../../../../helpers/entryUtils/manageTagsSentResponse";

// Custom components
import EntryScreenTemplate from "../../../../../../components/EntryScreenTemplate/EntryScreenTemplate";
import ImageViewer from "../../../../../../components/ImageViewer/ImageViewer";
import ManyPhotosDropzone from "../../../../../../components/ManyPhotosDropzone/ManyPhotosDropzone";
import EntryBrowser from "../../../../../../components/EntryBrowser/EntryBrowser";
import EntryBrowserModal from "../../../../../../components/EntryBrowserModal/EntryBrowserModal";
import ImageViewModal from "../../../../../../components/ImageViewModal/ImageViewModal";

// Other utilities
import cloneDeep from "lodash.clonedeep";
import toastManager from "../../../../../../helpers/toastManager";

function PhotoEntryScreen() {
  // Navigation and application state
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const patientId = useSelector(selectPatientId);
  const { id } = useParams();

  // API related
  const { response, error, axiosFetch } = useAxios();
  const apiUrl = `entries/photo_entry/${id}/`;

  // Entry data and related state
  const [entrySavedStatus, setEntrySavedStatus] = useState(false);
  const [entryData, setEntryData] = useState({});

  // Image items-related state
  const [files, setFiles] = useState([]);
  const [initialFiles, setInitialFiles] = useState([]);
  const [filesDetails, setFilesDetails] = useState([]);
  const [initialFilesDetails, setInitialFilesDetails] = useState([]);
  const [entryImages, setEntryImages] = useState([]);

  // AppointmentDate-related state
  const [appointmentDate, setAppointmentDate] = useState("");
  const [initialAppointmentDate, setInitialAppointmentDate] = useState("");

  // Note-related state
  const [notes, setNotes] = useState("");
  const [initialNotes, setInitialNotes] = useState("");

  // Summary-related state
  const [summary, setSummary] = useState("");
  const [initialSummary, setInitialSummary] = useState("");

  // Tag-related state
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [tags, setTags] = useState([]);
  const [initialTags, setInitialTags] = useState([]);

  // Image in image view modal
  const [modalImage, setModalImage] = useState(null);

  // Previous entires modal browser
  const [entryBrowserModalOpen, setEntryBrowserModalOpen] = useState(false);

  // FUNCTIONS START
  useEffect(() => {
    // Obtaining data from API on initial page load
    getEntryData();
  }, []);

  // * Primary entry functions
  const getEntryData = () => {
    axiosFetch({
      axiosInstance: multipartFormatAuth(),
      method: "GET",
      url: apiUrl,
      sendSignal: false,
      requestConfig: {},
    });
  };

  const saveEntry = () => {
    if (files.length === 0) {
      // Check if there are no images in the entry
      toastManager(
        "warning",
        "Warning",
        "Entry cannot be saved without containing any images. Add some images or delete this entry."
      );
      return;
    }

    const formData = new FormData();

    formData.append("appointment_date", appointmentDate);
    formData.append("notes", notes);
    formData.append("summary", summary);
    formData.append("classification_tags", JSON.stringify(tags));

    // Appending images as individual items
    appendPhotosToFormData({ formData, files, filesDetails });

    axiosFetch({
      axiosInstance: multipartFormatAuth(),
      method: "POST",
      url: apiUrl,
      sendSignal: false,
      requestConfig: formData,
    }).catch((error) => {
      // Handle errors here
      toastManager(
        "error",
        "Error",
        "There was an error sending the entry data. Please try again."
      );
    });
  };

  const deleteEntry = () => {
    axiosFetch({
      axiosInstance: jsonFormatAuth(),
      method: "DELETE",
      url: apiUrl,
      sendSignal: false,
      requestConfig: {
        // Attach delete_confirmation and if this is a rapid_entry
        data: { delete_confirmation: true, is_rapid_entry: false },
      },
    });
  };

  // * Secondary entry functions
  const hasDataChanged = () => {
    return (
      JSON.stringify(files) !== JSON.stringify(initialFiles) ||
      JSON.stringify(filesDetails) !== JSON.stringify(initialFilesDetails) ||
      appointmentDate !== initialAppointmentDate ||
      notes !== initialNotes ||
      summary !== initialSummary ||
      JSON.stringify(tags) !== JSON.stringify(initialTags)
    );
  };

  const handlePageLeave = () => {
    const leaveCondition = patientId;
    const activeLink = leaveCondition
      ? `/entries/photo_menu/${patientId}`
      : "/";
    navigate(activeLink);
    dispatch(setActiveMenu(leaveCondition ? "patients" : "home"));
    dispatch(setActivePage(activeLink));
  };

  const getTagSuggestions = () => {
    axiosFetch({
      axiosInstance: jsonFormatAuth(),
      method: "GET",
      url: "tags/autosuggestions/",
      sendSignal: false,
      requestConfig: {},
    });
  };

  const handleMenuRedirect = () => {
    const dataChanged = hasDataChanged();

    if (!dataChanged && files.length === 0) {
      // Empty and unchanged entry. Discard
      deleteEntry();
    } else if (dataChanged && !entrySavedStatus) {
      // Changed and unsaved entry. Prompt to try to save
      toastManager(
        "warning",
        "Warning",
        "Entry has unsaved changes. Save entry."
      );
    } else if (!dataChanged || entrySavedStatus) {
      handlePageLeave();
    }
  };
  // FUNCTIONS END

  // API START
  // RESPONSE Detection
  useEffect(() => {
    if (response) {
      // console.log("Response:");
      // console.log(response);

      const actionCode = response?.action_code;

      // Entry data received
      if (actionCode === "entry_sent") {
        // Request for tag suggestions
        getTagSuggestions();

        const entryDataResponse = response?.payload?.entry_data;

        if (entryDataResponse) {
          const sanitizedEntryDataResponse = sanitizePhotoEntryDataResponse({
            entryDataResponse,
          });
          setEntryData(sanitizedEntryDataResponse);

          const { apiImages, apiImagesDetails } = processPhotoEntryImages({
            sanitizedEntryDataResponse,
          });

          setAppointmentDate(sanitizedEntryDataResponse.appointment_date);
          setInitialAppointmentDate(
            sanitizedEntryDataResponse.appointment_date
          );

          // setTags([...sanitizedEntryDataResponse.tags]);
          // setInitialTags([...sanitizedEntryDataResponse.tags]);

          setTags([...sanitizedEntryDataResponse.classification_tags]);
          setInitialTags([...sanitizedEntryDataResponse.classification_tags]);

          setSummary(sanitizedEntryDataResponse.summary);
          setInitialSummary(sanitizedEntryDataResponse.summary);

          setNotes(sanitizedEntryDataResponse.notes);
          setInitialNotes(sanitizedEntryDataResponse.notes);

          setFiles([...apiImages]);
          setFilesDetails([...apiImagesDetails]);

          setInitialFiles(cloneDeep(apiImages));
          setInitialFilesDetails(cloneDeep(apiImagesDetails));
        }
      }

      // Entry successfully saved
      if (actionCode === "entry_saved") {
        toastManager(
          "success",
          "Entry Saved Successfully!",
          "All entry photos and data have been saved to the server."
        );
        setEntrySavedStatus(true);

        // Update values to detect changes
        setInitialTags([...tags]);
        setInitialAppointmentDate(appointmentDate);
        setInitialSummary(summary);
        setInitialNotes(notes);
        setInitialFiles(cloneDeep(files));
        setInitialFilesDetails(cloneDeep(filesDetails));
      }

      // Entry successfully deleted
      if (actionCode === "entry_deleted") {
        toastManager(
          "success",
          "Entry Deleted Successfully!",
          "All entry photos and data have been removed from the server."
        );
        handlePageLeave();
      }

      // Tag suggestions received
      if (actionCode === "tags_sent") {
        manageTagsSentResponse({ response, setTagSuggestions });
      }
    }
  }, [response]);

  // ERROR Detection
  useEffect(() => {
    if (error) {
      // console.log("Error:");
      // console.log(error);

      const actionCode = error?.action_code;

      // Entry does not exist
      if (actionCode === "entry_not_exist" || error?.pk) {
        toastManager(
          "error",
          "Entry does not exist!",
          "Entry not found or not owned by the user."
        );
        handlePageLeave();
      }
    }
  }, [error]);
  // API END

  return (
    <EntryScreenTemplate
      entryTitle={"Photo Entry"}
      // backLink={`/entries/photo_menu/${patientId}`}
      handleMenuRedirect={handleMenuRedirect}
      appointmentDate={appointmentDate}
      setAppointmentDate={setAppointmentDate}
      suggestionList={tagSuggestions}
      tags={tags}
      setTags={setTags}
      notes={notes}
      setNotes={setNotes}
      summary={summary}
      setSummary={setSummary}
      saveEntryFunction={saveEntry}
      deleteEntryFunction={deleteEntry}
      entryChanged={hasDataChanged()}
      entryData={entryData}
      getEntryData={getEntryData}
    >
      <div className="flex flex-col items-center justify-center w-full h-full Xbg-red-500">
        {/* <h6>{JSON.stringify(files)}</h6> */}
        {/* <h6>{JSON.stringify(filesDetails)}</h6> */}
        {modalImage && (
          <ImageViewModal exitFunction={setModalImage} setValue={null}>
            <ImageViewer modalImage={modalImage} />
          </ImageViewModal>
        )}

        {entryBrowserModalOpen && (
          <EntryBrowserModal
            exitFunction={setEntryBrowserModalOpen}
            setValue={false}
          >
            <EntryBrowser
              patientId={patientId}
              photoEntry={true}
              files={files}
              setFiles={setFiles}
              filesDetails={filesDetails}
              setFilesDetails={setFilesDetails}
              setEntryBrowserModalOpen={setEntryBrowserModalOpen}
            />
          </EntryBrowserModal>
        )}

        <ManyPhotosDropzone
          entryImages={entryImages}
          setEntryImages={setEntryImages}
          files={files}
          setFiles={setFiles}
          filesDetails={filesDetails}
          setFilesDetails={setFilesDetails}
          modalImage={modalImage}
          setModalImage={setModalImage}
          entryBrowserModalOpen={entryBrowserModalOpen}
          setEntryBrowserModalOpen={setEntryBrowserModalOpen}
        />
      </div>
    </EntryScreenTemplate>
  );
}

export default PhotoEntryScreen;
